import _ from 'lodash';
import { forwardRef, useImperativeHandle } from 'react';
import moment from 'moment';
import { fetchListEcgBookmarks, handleDeleteEcgBookmarks } from '@/entities/bookmark';
import { handleRequestFileUpload } from '@/shared/api';
import { handleUpdateAfibHrSummary } from '@/entities/heart-rate';
import { handleReviewHolterEvents } from '@/entities/event';
import {
  handleUpdateBeatsAndEvents,
  addIsSavingToChangedData,
  defaultBeatsOptions,
} from '@/entities/beat';
import { handleUpdateHolterProfile } from '@/entities/study';
import { ANNOTATION_TYPE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  AIRP_TAB,
  ID_BOOKMARK_TAB,
  KEY_RECOIL,
  TYPE_BEAT_ENUM,
  TYPE_EVENT_OTHER,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import { updateStatusAndMetadata, isValidHttpUrl, putFileToUrl } from '@/shared/lib';
import { buildGroupEventsPayload, buildReviewHolterEventsPayload } from '@/shared/lib/aiUtils';
import emitter from '@/shared/lib/eventEmitter';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  defaultEventOptions,
  selectActiveTab,
  selectBeatChanges,
  selectEventChanges,
  selectEventNews,
  selectEventOthers,
  selectGroupEventChanges,
  useReportStore,
} from '@/entities/report';
import { logError } from '@/shared/lib/apiHelpers';

export type SummaryFunctionHandle = {
  onClickSaveReport: () => Promise<void>;
  isChangedData: () => boolean;
  resetData: () => void;
};

type SummaryFunctionProps = {
  studyId?: string;
  profileId?: string;
  isNotEdit?: boolean;
  // dead prop: index.tsx passes this but SummaryFunction never reads it.
};

/**
 * Headless save controller for the ECG Events tab, exposing onClickSaveReport, isChangedData and resetData to the report shell through a forwarded ref. Wires the TAB_2 report-store getters and setters, uploads freshly drawn strip SVGs, then fans the save out to beats and events, holter review flags, bookmark deletions, the AFib heart rate and the holter profile.
 *
 * @param props - The owning studyId and profileId plus the isNotEdit read-only flag.
 * @param ref - Forwarded ref that receives the save, dirty-check and reset handle.
 * @returns Null - the component renders no markup and works entirely through its imperative handle.
 */
const SummaryFunction = forwardRef<SummaryFunctionHandle, SummaryFunctionProps>(
  ({ studyId, profileId, isNotEdit }, ref) => {
    const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
    /**
     * Reads the event-type tab currently active on the ECG Events tab. Applies the selectActiveTab selector bound to the TAB_2 key against a one-off report-store snapshot instead of subscribing to it.
     *
     * @returns The active rhythm, beat, or beat-HR tab identifier.
     */
    const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
    /**
     * Reads the pending edits to Other-type events on the ECG Events tab. Applies the selectEventOthers selector bound to the TAB_2 key to a one-off report-store snapshot.
     *
     * @returns The list of pending Other-event entries.
     */
    const getEventOthers = () => selectEventOthers(keyRecoil)(useReportStore.getState());
    const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
    /**
     * Replaces the pending Other-type event entries of the ECG Events tab. Forwards the value to the store's setEventOthers action with the TAB_2 key prepended.
     *
     * @param v - The new Other-event list, or an updater receiving the current one.
     * @returns Nothing.
     */
    const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
      setEventOthersAction(keyRecoil, v);
    /**
     * Reads the pending edits to existing rhythm events on the ECG Events tab. Applies the selectEventChanges selector bound to the TAB_2 key to a one-off report-store snapshot.
     *
     * @returns The list of changed events.
     */
    const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
    const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
    /**
     * Replaces the pending edits to existing events on the ECG Events tab. Forwards the value to the store's setEventChanges action with the TAB_2 key prepended.
     *
     * @param v - The new event-change list, or an updater receiving the current one.
     * @returns Nothing.
     */
    const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
      setEventChangesAction(keyRecoil, v);
    /**
     * Reads the events newly created on the ECG Events tab but not yet saved. Applies the selectEventNews selector bound to the TAB_2 key to a one-off report-store snapshot.
     *
     * @returns The list of newly created events.
     */
    const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
    const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
    /**
     * Replaces the newly created events held by the ECG Events tab. Forwards the value to the store's setEventNews action with the TAB_2 key prepended.
     *
     * @param v - The new event list, or an updater receiving the current one.
     * @returns Nothing.
     */
    const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
      setEventNewsAction(keyRecoil, v);
    /**
     * Reads the pending group-level event changes of the ECG Events tab. Applies the selectGroupEventChanges selector bound to the TAB_2 key to a one-off report-store snapshot.
     *
     * @returns The list of pending group event changes.
     */
    const getGroupEventChanges = () =>
      selectGroupEventChanges(keyRecoil)(useReportStore.getState());
    const setGroupEventChangesAction = useReportStore((s) => s.actions.setGroupEventChanges);
    /**
     * Replaces the pending group-level event changes of the ECG Events tab. Forwards the value to the store's setGroupEventChanges action with the TAB_2 key prepended.
     *
     * @param v - The new group-event-change list, or an updater receiving the current one.
     * @returns Nothing.
     */
    const setGroupEventChanges = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
      setGroupEventChangesAction(keyRecoil, v);
    /**
     * Reads the pending beat edits accumulated on the ECG Events tab. Applies the selectBeatChanges selector bound to the TAB_2 key to a one-off report-store snapshot instead of subscribing.
     *
     * @returns The list of pending beat changes.
     */
    const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
    const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
    /**
     * Replaces the pending beat edits held for the ECG Events tab. Forwards the value to the store's setBeatChanges action with the TAB_2 key prepended.
     *
     * @param v - The new beat-change list, or an updater receiving the current one.
     * @returns Nothing.
     */
    const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
      setBeatChangesAction(keyRecoil, v);
    const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
    /**
     * Closes every ECG Events modal at once by resetting the tab's control-modal flags. Delegates to the store's resetControlModal action bound to the TAB_2 key.
     *
     * @returns Nothing.
     */
    const resetControlModal = () => resetControlModalAction(keyRecoil);
    /**
     * Reads the technician-edited average heart rate for atrial fibrillation. Pulls updatedAfibAvgHr directly off a report-store snapshot without subscribing.
     *
     * @returns The edited AFib average heart rate.
     */
    const getUpdatedAfibAvgHr = () => useReportStore.getState().updatedAfibAvgHr;
    const setUpdatedAfibAvgHr = useReportStore((s) => s.actions.setUpdatedAfibAvgHr);
    /**
     * Reads the ids of ECG bookmarks the user has marked for deletion. Pulls ecgBookmarkDeleteIds straight off a report-store snapshot, bypassing any subscription.
     *
     * @returns The ids of bookmarks queued for deletion.
     */
    const getEcgBookmarkDeletedIds = () => useReportStore.getState().ecgBookmarkDeleteIds;
    const setEcgBookmarkDeleteIds = useReportStore((s) => s.actions.setEcgBookmarkDeleteIds);
    const setEcgBookmarks = useReportStore((s) => s.actions.setEcgBookmarks);
    const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
    /**
     * Moves the ECG Events list to another page. Forwards the value to the store's setPageIndex action with the TAB_2 key prepended.
     *
     * @param v - The new page index, or an updater receiving the current one.
     * @returns Nothing.
     */
    const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
      setPageIndexAction(keyRecoil, v);
    const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
    /**
     * Replaces the event options snapshot the ECG Events tab keeps for undo and redo. Forwards the value to the store's setEventOptions action with the TAB_2 key prepended.
     *
     * @param v - The new event options, or an updater receiving the current ones.
     * @returns Nothing.
     */
    const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
      setEventOptionsAction(keyRecoil, v);
    /**
     * Reads the metadata describing the study and holter profile currently open. Pulls reportInfo directly off a report-store snapshot, which stays an empty object until the report has loaded.
     *
     * @returns The report info, or an empty object before it loads.
     */
    const getReportInfo = () => useReportStore.getState().reportInfo;
    const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
    /**
     * Replaces the beat options snapshot the ECG Events tab keeps for undo and redo. Forwards the value to the store's setBeatOptions action with the TAB_2 key prepended.
     *
     * @param v - The new beat options, or an updater receiving the current ones.
     * @returns Nothing.
     */
    const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
      setBeatOptionsAction(keyRecoil, v);
    const setIsPushQueue = useReportStore((s) => s.actions.setIsPushQueue);
    /**
     * Builds the beat mutation payload for a save of the ECG Events tab. Maps each beat to its time, a wire type where DELETED becomes Del and Q becomes Others, and an isNew flag derived from oldType, then marks the beat changes and the undo/redo beat-option snapshot as saving.
     *
     * @param beats - The pending beat changes; an empty array skips the payload entirely.
     * @returns A promise resolving to an object holding the mapped beats, or undefined when there are no beat changes.
     */
    const getInputUpdateBeats = async (beats: any[]) => {
      if (beats.length === 0) {
        return undefined;
      }
      const input = {
        beats: _.map(beats, (k) => ({
          time: k?.id,
          type:
            k?.type === TYPE_BEAT_ENUM.DELETED
              ? 'Del'
              : k?.type === TYPE_BEAT_ENUM.Q
                ? 'Others'
                : k?.type,
          isNew: k.oldType === undefined || k.oldType === k.type,
        })),
      };
      setBeatChanges(addIsSavingToChangedData(beats));
      setBeatOptions({
        beatChangesState: addIsSavingToChangedData(getBeatChanges()),
      });
      return input;
    };

    /**
     * Prepares every changed rhythm event for saving and uploads the strip images drawn in the browser. Merges changed, new and touched Other events, collects linked event ids, requests presigned svg URLs for captures whose stripImgs are not yet http URLs, PUTs each image, rewrites the capture with the returned URL and sample-mark times, then builds the group-event and holter-review payloads.
     *
     * @param eChanges - Pending edits to existing events.
     * @param eNews - Newly created events.
     * @param eOthers - Pending Other-event entries; only those flagged as updated, removed or image-changed are kept.
     * @param groupEventChanges - Group-level event changes used to build the group and review payloads.
     * @returns A promise resolving to an object with changedEvents, allLinkEventIds, updateGroupEvents, reviewedEvents and unReviewedEvents, or an empty object if the upload step threw.
     */
    const formatEventsAndUploadCapturedImage = async (
      eChanges: any[] = [],
      eNews: any[] = [],
      eOthers: any[] = [],
      groupEventChanges: any[] = [],
    ) => {
      try {
        const eventOthers = _.filter(
          eOthers,
          (x) => !!x.isUpdatedOther || !!x.isRemovedStrip || !!x.isUpdateImage,
        );
        const changedEvents = _.cloneDeep([...eChanges, ...eNews, ...eventOthers]);
        const allLinkEventIds: any[] = [];
        const tempEventStripUpdate: any[] = [];
        const tempArrImgUpload: any[] = [];
        changedEvents.forEach((x, index) => {
          if (x.linkEvents?.length) {
            x.linkEvents.forEach((linkEvent: any) => allLinkEventIds.push(linkEvent.idEvent));
          }
          //* if not valid url => is svg => have updated strip
          _.forEach(x.captures, (capture) => {
            if (capture.stripImgs?.length && !isValidHttpUrl(capture.stripImgs[0])) {
              tempEventStripUpdate.push({
                index,
                captureId: capture.captureId,
                imagesCount: capture.stripImgs.length,
              });
              tempArrImgUpload.push(...capture.stripImgs);
            }
          });
        });
        if (changedEvents.length && tempArrImgUpload.length) {
          // Perform grouping action applicable to undo/redo
          setEventOptions((prevOptions: any) => ({
            ...prevOptions,
            eventChangesState: addIsSavingToChangedData(getEventChanges()),
          }));
          setEventChanges(addIsSavingToChangedData(getEventChanges()));
          const uploadFileUrls = await handleRequestFileUpload('svg', tempArrImgUpload.length);
          if (uploadFileUrls?.length) {
            const putFileToUrlPromises = _.map(uploadFileUrls, (uploadFileUrl, index) =>
              putFileToUrl(uploadFileUrl, tempArrImgUpload[index], false),
            );
            await Promise.all(putFileToUrlPromises);
            _.forEach(tempEventStripUpdate, (data) => {
              const foundUpdatedStripEvent = changedEvents[data.index];
              const findCapture = _.find(
                foundUpdatedStripEvent.captures,
                (obj) => obj.captureId === data.captureId,
              );
              const findDraw = _.find(
                findCapture,
                (obj) => obj.type === ANNOTATION_TYPE.SAMPLE_MARK,
              );
              if (findDraw) {
                findCapture.start = findDraw.startTime;
                findCapture.stop = findDraw.endTime;
              }
              for (let i = 0; i < data.imagesCount; i += 1) {
                if (!_.isArray(findCapture.stripImgs)) {
                  findCapture.stripImgs = [uploadFileUrls.shift()];
                } else {
                  findCapture.stripImgs[i] = uploadFileUrls.shift();
                }
              }
            });
          }
        }

        const updateGroupEvents = buildGroupEventsPayload(groupEventChanges);
        const { reviewedEvents, unReviewedEvents } =
          buildReviewHolterEventsPayload(groupEventChanges);
        return {
          changedEvents,
          allLinkEventIds,
          updateGroupEvents,
          reviewedEvents,
          unReviewedEvents,
        };
      } catch (error) {
        console.error('Failed to update image', error);
        return {};
      }
    };

    /**
     * Builds the event mutation payload for a save of the ECG Events tab. Maps Other events onto a strip create-or-update shape, maps rhythm events to id, start, stop and type while translating DELETED and ARTIFACT into isDeleted or isMarkedAsArtifact and restoring oldType for linked deletions, expands changed captures into strip entries, then flags the changed, new and Other event lists plus their undo/redo snapshots as saving.
     *
     * @param changedEvents - The merged list of changed, new and Other events to serialize.
     * @param allLinkEventIds - Ids of events linked to a changed one, used to restore the original type instead of deleting.
     * @param updateGroupEvents - Group-level event payload; included only when non-empty.
     * @returns A promise resolving to an object with updateGroupEvents and updateEvents, or undefined when nothing changed.
     */
    const getInputUpdateEvents = async ({
      changedEvents = [],
      allLinkEventIds = [],
      updateGroupEvents,
    }: any) => {
      const hasGroupEventChanges = !_.isEmpty(updateGroupEvents);
      const hasChangedEvents = changedEvents.length > 0;
      if (!hasChangedEvents && !hasGroupEventChanges) {
        return undefined;
      }
      const input = {
        updateGroupEvents: !_.isEmpty(updateGroupEvents) ? updateGroupEvents : undefined,
        updateEvents: _.map(changedEvents, (k) => {
          if (k.type === TYPE_EVENT_OTHER) {
            const strip = k.captures?.[0];
            const isUpdatedOldEvent = k.isUpdatedOther && k.idEvent;
            const sampleMarkDraw = _.find(k.draw, (x) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
            return {
              start: k.start,
              stop: k.stop,
              type: k.type,
              title: k.title,
              isIncludedToReport: isUpdatedOldEvent
                ? k.isIncludedToReport
                : !_.isEmpty(k.stripImgs),
              id: k.idEvent || undefined,
              isDeleted: k.isRemovedStrip || undefined,
              strips: strip?.originalId
                ? [
                    {
                      comment: k.technicianComments ?? undefined,
                      action: k.isRemovedStrip ? 'delete' : k.action || 'update',
                      id: strip?.originalId || undefined,
                      channel: strip?.isUpdateOtherImage
                        ? (sampleMarkDraw.channelIndex || 0) + 1
                        : undefined,
                      draw: strip?.isUpdateOtherImage ? k.draw : undefined,
                      images: strip?.isUpdateOtherImage ? k.stripImgs : undefined,
                    },
                  ]
                : [
                    {
                      start: k.stripStart ?? k.start,
                      stop: k.stripStop ?? k.stop,
                      images: k.stripImgs,
                      isSelected: !_.isEmpty(k.stripImgs),
                      comment: k.technicianComments ?? undefined,
                      draw: k.draw,
                      action: k.isRemovedStrip ? 'delete' : k.action || 'update',
                      channel: (sampleMarkDraw?.channelIndex || 0) + 1,
                      id: strip?.id || undefined,
                    },
                  ],
            };
          }
          const formatUpdateEvent: any = {
            id: k.idEvent,
            start: k.start ? moment(k.start).toISOString() : undefined,
            stop: k.stop ? moment(k.stop).toISOString() : undefined,
            isReviewed: k.isReviewed,
            strips: k.isUpdateImage ? [] : undefined,
          };
          if ([TYPE_BEAT_ENUM.DELETED, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT].includes(k.type)) {
            if (allLinkEventIds.includes(k.idEvent) && k.type === TYPE_BEAT_ENUM.DELETED) {
              formatUpdateEvent.type = k.oldType;
            } else {
              formatUpdateEvent[
                k.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ? 'isMarkedAsArtifact' : 'isDeleted'
              ] = true;
            }
          } else {
            formatUpdateEvent.type = k.type;
          }
          if (k.isUpdateImage) {
            _.forEach(
              _.filter(
                [...k.captures, ...(k.removedCaptures || [])],
                (x) => !!x.action || k.isRemovedStrip,
              ),
              (capture) => {
                const findDraw = _.find(
                  capture.draw,
                  (obj) => obj.type === ANNOTATION_TYPE.SAMPLE_MARK,
                );
                const { originalStrip } = capture || {};
                if (k.isRemovedStrip && capture.action === 'delete') {
                  formatUpdateEvent.strips.push({
                    action: 'delete',
                    id: capture.originalId,
                  });
                } else if (!_.isEmpty(originalStrip)) {
                  formatUpdateEvent.strips.push({
                    id: capture.originalId,
                    action: capture.action,
                    comment:
                      originalStrip.comment === capture.technicianComments
                        ? undefined
                        : capture.technicianComments,
                    start: _.isEqual(capture.stripImgs, originalStrip.images)
                      ? undefined
                      : findDraw.startTime,
                    stop: _.isEqual(capture.stripImgs, originalStrip.images)
                      ? undefined
                      : findDraw.endTime,
                    images: _.isEqual(capture.stripImgs, originalStrip.images)
                      ? undefined
                      : capture.stripImgs,
                    draw: _.isEqual(capture.stripImgs, originalStrip.images)
                      ? undefined
                      : capture.draw,
                    channel: _.isEqual(capture.stripImgs, originalStrip.images)
                      ? undefined
                      : (findDraw?.channelIndex || 0) + 1,
                  });
                } else {
                  formatUpdateEvent.strips.push({
                    start: findDraw.startTime,
                    stop: findDraw.endTime,
                    images: capture.stripImgs,
                    isSelected: !_.isEmpty(capture.stripImgs),
                    comment: capture.technicianComments ?? undefined,
                    draw: capture.draw,
                    action: k.isRemovedStrip ? 'delete' : capture.action,
                    id: capture.originalId || undefined,
                    channel: (findDraw?.channelIndex || 0) + 1,
                  });
                  _.assign(formatUpdateEvent, {
                    isIncludedToReport: !_.isEmpty(capture.stripImgs),
                  });
                }
              },
            );
          }
          return formatUpdateEvent;
        }),
      };
      console.log('[beatHr/summaryFunction]-updateEvents', input);
      // Perform grouping action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventNewsState: addIsSavingToChangedData(getEventNews()),
        eventChangesState: addIsSavingToChangedData(getEventChanges()),
        eventOthersState: addIsSavingToChangedData(getEventOthers()),
      }));
      setEventOthers(addIsSavingToChangedData(getEventOthers()));
      setEventChanges(addIsSavingToChangedData(getEventChanges()));
      setEventNews(addIsSavingToChangedData(getEventNews()));
      return input;
    };

    /**
     * Persists the technician-edited AFib average heart rate for the current study. Skips the call when no value was edited, otherwise sends studyId, profileId and the heart rate through handleUpdateAfibHrSummary, reporting failure with a logged error and an error toast.
     *
     * @param afibAvgHr - The edited atrial-fibrillation average heart rate to persist; a falsy value skips the request.
     * @returns A promise resolving to true once the update succeeds, or false when there was nothing to send or the request failed.
     */
    const updateAfibHr = async (afibAvgHr: any) => {
      try {
        if (!afibAvgHr) return false;
        await handleUpdateAfibHrSummary({
          studyId,
          afibAvgHr,
          profileId,
        });
        return true;
      } catch (error) {
        logError('Failed to save: ', error);
        toastrError(error.message, 'Error');
        return false;
      }
    };

    /**
     * Deletes the ECG bookmarks the user removed and refreshes the bookmark list. Skips when the id list is empty, otherwise deletes them, refetches the study's bookmarks ascending from offset 0, clears the pending delete ids and rewinds to the first page while the bookmark tab is active.
     *
     * @param listIds - Ids of the bookmarks to delete; an empty list skips the request.
     * @returns A promise resolving to false in every case, because bookmark deletion expects no socket confirmation.
     */
    const updateEcgBookmarks = async (listIds: any[]) => {
      try {
        if (_.isEmpty(listIds)) return false;
        await handleDeleteEcgBookmarks(listIds);
        const ecgBookmarks = await fetchListEcgBookmarks(
          {
            studyId,
            sortOrder: 'asc',
            skip: 0,
            profileId,
          },
          0,
        );
        setEcgBookmarkDeleteIds([]);
        setEcgBookmarks(ecgBookmarks as any);
        // activeTabState also allows TYPE_RHYTHM_EVENT_ENUM/TYPE_BEAT_EVENT_ENUM, none of which include ID_BOOKMARK_TAB's literal - atom-type gap.
        if ((getActiveButton() as any) === ID_BOOKMARK_TAB) {
          setPageIndex({ index: 0 });
        }
        //* Ecg bookmark dont need socket response so return false
        return false;
      } catch (error) {
        logError('Failed to save: ', error);
        toastrError(error.message, 'Error');
        return false;
      }
    };

    /**
     * Sends the combined beat and event edits of the ECG Events tab to the backend. Bails out when beats, events, linked ids and group changes are all empty, otherwise builds the beat and event payloads, wraps them with studyId, profileId, the ECG_EVENTS tab marker and requestId, and posts them through handleUpdateBeatsAndEvents, swallowing failures to the console.
     *
     * @param info - An object carrying beatChanges, changedEvents, allLinkEventIds, updateGroupEvents and the correlating requestId.
     * @returns A promise resolving to true when the mutation was sent, or false when there was nothing to send or the request threw.
     */
    const updateBeatsAndEvents = async (info: any) => {
      const { beatChanges, changedEvents, allLinkEventIds, updateGroupEvents, requestId } =
        info || {};
      if (
        beatChanges.length === 0 &&
        changedEvents.length === 0 &&
        _.isEmpty(allLinkEventIds) &&
        _.isEmpty(updateGroupEvents)
      ) {
        return false;
      }
      const beatData = await getInputUpdateBeats(beatChanges);
      const eventData = await getInputUpdateEvents({
        changedEvents,
        allLinkEventIds,
        updateGroupEvents,
      });
      try {
        const input = {
          studyId,
          profileId,
          tab: AIRP_TAB.ECG_EVENTS,
          requestId,
          beatData,
          eventData,
        };
        await handleUpdateBeatsAndEvents(input);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    };

    /**
     * Persists the reviewed and un-reviewed flags for holter events changed in bulk. Destructures reviewedEvents, unReviewedEvents and requestId off the argument, bails out when both lists are empty, and otherwise posts them with studyId and profileId through handleReviewHolterEvents, swallowing failures to the console.
     *
     * @param info - An object carrying reviewedEvents, unReviewedEvents and the requestId that correlates the socket response.
     * @returns A promise resolving to true when the review update was sent, or false when there was nothing to send or the request threw.
     */
    const updateReviewHolterEvents = async (info: any) => {
      const { reviewedEvents = [], unReviewedEvents = [], requestId } = info || {};
      if (_.isEmpty(reviewedEvents) && _.isEmpty(unReviewedEvents)) {
        return false;
      }
      try {
        const input = {
          studyId,
          profileId,
          requestId,
          reviewedEvents,
          unReviewedEvents,
        };
        await handleReviewHolterEvents(input);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    };

    /**
     * Reports whether the ECG Events tab holds any unsaved edit across beats, events, group events, bookmark deletions and the AFib average heart rate. Collects each pending list from the store and, when invoked for a save click, first drops entries already flagged isSaving, then ORs the remaining lengths together with the edited AFib heart rate.
     *
     * @param isClickSave - When true, entries already being saved are excluded so an in-flight save is not counted as a pending change.
     * @returns True when some pending list is non-empty, otherwise the edited AFib average heart rate itself, which is falsy when unset.
     */
    const checkChangedData = (isClickSave = false) => {
      const beatChanges = getBeatChanges();
      const eventChanges = getEventChanges();
      const eventNews = getEventNews();
      const groupEventChanges = getGroupEventChanges();
      const eventOthers = getEventOthers();
      const updatedAfibAvgHr = getUpdatedAfibAvgHr();
      const ecgBookmarkDeleteIds = getEcgBookmarkDeletedIds();

      const filterNotSavingNews = isClickSave
        ? _.filter(eventNews, (x: any) => !x.isSaving)
        : eventNews;
      const filterNotSavingChanges = isClickSave
        ? _.filter(eventChanges, (x: any) => !x.isSaving)
        : eventChanges;
      const filterNotSavingOthers = isClickSave
        ? _.filter(eventOthers, (x: any) => !x.isSaving)
        : eventOthers;
      const filterNotSavingBeats = isClickSave
        ? _.filter(beatChanges, (x: any) => !x.isSaving)
        : beatChanges;

      return (
        !!filterNotSavingBeats.length ||
        !!filterNotSavingChanges.length ||
        !!filterNotSavingNews.length ||
        !!groupEventChanges.length ||
        !!filterNotSavingOthers.length ||
        !!ecgBookmarkDeleteIds.length ||
        updatedAfibAvgHr
      );
    };

    useImperativeHandle(ref, () => ({
      onClickSaveReport: async () => {
        if (!checkChangedData(true) && !isNotEdit) return;
        setIsPushQueue(true);
        emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: true, isSaving: true });
        const requestId = `requests:${profileId}:${Date.now()}`;

        const {
          changedEvents,
          allLinkEventIds,
          updateGroupEvents,
          reviewedEvents,
          unReviewedEvents,
        } = await formatEventsAndUploadCapturedImage(
          getEventChanges(),
          getEventNews(),
          getEventOthers(),
          getGroupEventChanges(),
        );
        const info: any = getReportInfo();
        const lastModified = moment();

        const keyStorage = `requestId-${info.profileId}-${info.studyFid}`;
        let requestData: any = localStorage.getItem(keyStorage);
        if (_.isEmpty(requestData) || !Array.isArray(requestData)) {
          requestData = [
            {
              requestId,
              type: 'RHYTHM_EVENT',
            },
          ];
        } else {
          requestData.push({
            requestId,
            type: 'RHYTHM_EVENT',
          });
        }
        localStorage.setItem(keyStorage, JSON.stringify(requestData));
        // pre-existing: localStorage.setItem's 2nd arg must be a string; these pass a boolean.
        localStorage.setItem(`ai_process-${info.profileId}`, false as any);
        localStorage.setItem(`ai_saving-${info.profileId}`, true as any);
        const updateHolterProfileInput = {
          id: info.profileId,
          lastModified,
        };

        const isUpdateBeatsAndEvents = await updateBeatsAndEvents({
          beatChanges: getBeatChanges(),
          changedEvents,
          allLinkEventIds,
          updateGroupEvents,
          requestId,
        });
        const hasReviewAction = !_.isEmpty(reviewedEvents) || !_.isEmpty(unReviewedEvents);
        // requestId should diffrerent because BE check 2 cmd
        const isReviewHolterEvents = hasReviewAction
          ? await updateReviewHolterEvents({
              reviewedEvents,
              unReviewedEvents,
              requestId: `requests:${profileId}:${Date.now()}`,
            })
          : false;

        const promise = [
          updateEcgBookmarks(getEcgBookmarkDeletedIds()),

          updateAfibHr(getUpdatedAfibAvgHr()),
          handleUpdateHolterProfile(updateHolterProfileInput),
        ];
        updateStatusAndMetadata({
          lastModified,
        } as any);
        const [isSuccessEcgBookmark, isSuccessAfibHr] = await Promise.all(promise);
        if (
          !(
            isSuccessEcgBookmark ||
            isSuccessAfibHr ||
            isUpdateBeatsAndEvents ||
            isReviewHolterEvents
          )
        ) {
          emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false });
          emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
        } else {
          resetControlModal();
        }
        setEventOptions(defaultEventOptions);
      },

      /**
       * Tells the parent report shell whether the ECG Events tab has unsaved work. Combines the full unsaved-change scan with the read-only isNotEdit prop so a locked report never reports itself dirty.
       *
       * @returns True when unsaved changes exist and the tab is editable.
       */
      isChangedData() {
        return checkChangedData() && !isNotEdit;
      },

      /**
       * Discards every unsaved change on the ECG Events tab. Clears the beat, event, new-event, group-event and Other-event lists plus the bookmark delete ids, unsets the edited AFib heart rate, and restores the default event and beat option snapshots.
       *
       * @returns Nothing.
       */
      resetData() {
        setBeatChanges([]);
        setEventChanges([]);
        setEventNews([]);
        setGroupEventChanges([]);
        setEventOthers([]);
        setEcgBookmarkDeleteIds([]);
        setUpdatedAfibAvgHr(undefined);
        setEventOptions(defaultEventOptions);
        setBeatOptions(defaultBeatsOptions);
      },
    }));

    return null;
  },
);

export default SummaryFunction;
